/** @jsx jsx */

import { ReactNode, SyntheticEvent, useState } from 'react';

import {
  Table,
  TableBody,
  TableHead,
  TableHeadSortButton,
  TableRow,
  TableTh,
  Text,
} from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

const Styles = {
  row: css`
    cursor: pointer;
  `,
  cell: ({ center, right, disabled, hidden, indent, wrap }: CellModifiers) => [
    css`
      font-weight: 300;
      line-height: 1.5;
      padding: 0.5em 1em;
      :first-of-type {
        padding-left: 0px;
      }

      :last-of-type {
        padding-right: 0px;
        text-align: right;
      }
    `,
    center &&
      css`
        text-align: center;
      `,
    right &&
      css`
        text-align: right;
      `,
    disabled &&
      css`
        cursor: not-allowed;
      `,
    hidden &&
      css`
        display: none;
      `,
    indent &&
      css`
        :first-of-type {
          padding-left: 0.5em;
        }
        :last-of-type {
          padding-right: 0.5em;
          text-align: right;
        }
      `,
    wrap &&
      css`
        word-break: break-all;
      `,
  ],
  tableHeader: css`
    white-space: nowrap;
  `,
};

type CellModifiers = {
  center?: boolean;
  right?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  indent?: boolean;
  wrap?: boolean;
};

type BodyColumn = {
  value: string | number;
  element: string | number | ReactNode;
};

type BodyRow = BodyColumn[];

type Props = {
  body: BodyRow[];
  header: (
    | {
        element: ReactNode;
        hasSort?: boolean;
      }
    | {
        hasSort?: boolean;
        title: string;
      }
  )[];
  getCellModifiers?: (iCol: number, iRow: number) => CellModifiers;
  onClickRow: (event: SyntheticEvent<any, Event>, index: number) => void;
  rowAriaLabel: string;
};

const defaultProps = {
  getCellModifiers: () => ({}),
};

function ClickableTable({
  body,
  header,
  onClickRow,
  getCellModifiers = defaultProps.getCellModifiers,
  rowAriaLabel,
}: Props) {
  // No sorted column and no reverse initially.
  const [sortedColumnIndex, setSortedColumnIndex] = useState<number>(undefined);
  const [isReversed, setIsReversed] = useState<boolean>(undefined);

  const bodyRowsIndexed: [BodyRow, number][] = body.map((row, iRow) => [
    row,
    iRow,
  ]);

  const renderedBodyRowsIndexed =
    sortedColumnIndex !== undefined
      ? [...bodyRowsIndexed].sort(([row1], [row2]) => {
          const { value: value1 } = row1[sortedColumnIndex];
          const { value: value2 } = row2[sortedColumnIndex];

          return typeof value2 === 'number'
            ? Number(value1) - Number(value2)
            : String(value1)?.localeCompare(String(value2), undefined, {
                numeric: true,
              });
        })
      : bodyRowsIndexed;

  if (isReversed) {
    renderedBodyRowsIndexed.reverse();
  }

  const onSortBy = (sortedColumnIndexNext: number) => {
    // No reverse when sorting a new column. Toggle reverse otherwise.
    if (sortedColumnIndex === sortedColumnIndexNext) {
      setIsReversed(isReversed === undefined ? false : !isReversed);
    } else {
      setIsReversed(false);
      setSortedColumnIndex(sortedColumnIndexNext);
    }
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          {header.map((col, index) => {
            const colElement = (
              <Text css={Styles.tableHeader} type="Body3">
                {'element' in col ? col.element : col.title}
              </Text>
            );
            return (
              <TableTh key={index}>
                {col.hasSort ? (
                  <TableHeadSortButton onClick={() => onSortBy(index)}>
                    {colElement}
                  </TableHeadSortButton>
                ) : (
                  colElement
                )}
              </TableTh>
            );
          })}
        </TableRow>
      </TableHead>
      {!!body.length && (
        <TableBody>
          {renderedBodyRowsIndexed.map(bodyRowIndexed => (
            <tr
              aria-label={rowAriaLabel}
              css={Styles.row}
              key={bodyRowIndexed[1]}
              onClick={event => onClickRow(event, bodyRowIndexed[1])}
            >
              {bodyRowIndexed[0].map((col, iCol) => (
                <td
                  css={Styles.cell(getCellModifiers(iCol, bodyRowIndexed[1]))}
                  key={iCol}
                >
                  {['string', 'number'].includes(typeof col.element) ? (
                    <Text>{col.element}</Text>
                  ) : (
                    col.element
                  )}
                </td>
              ))}
            </tr>
          ))}
        </TableBody>
      )}
    </Table>
  );
}

ClickableTable.defaultProps = defaultProps;

export default ClickableTable;
